import { Skeleton } from '@mui/material';
import React, { useEffect, useState } from 'react';

import {
  CardContentCard as CardContentCardModel,
  CardInfoCard as CardInfoCardModel,
  CardMetaCard as CardMetaCardModel,
  CardProductCard as CardProductCardModel,
  CardServiceCard as CardServiceCardModel,
  ContentCard as ContentCardModel,
  InfoCard as InfoCardModel,
  isCardContentCard,
  isCardInfoCard,
  isCardMetaCard,
  isCardProductCard,
  isCardServiceCard,
  isContentCard,
  isInfoCard,
  isMetaCard,
  isProductCard,
  isServiceCard,
  MetaCard as MetaCardModel,
  ProductCard as ProductCardModel,
  ServiceCard as ServiceCardModel,
} from '../../../models';
import { ContentCard, ContentCardVariants } from '../../components/ContentCard/ContentCard';
import { InfoCard } from '../../components/InfoCard/InfoCard';
import { MetaCard } from '../../components/MetaCard/MetaCard';
import { ProductCard, ProductCardVariants } from '../../components/ProductCard/ProductCard';
import { ServiceCard, ServiceCardVariants } from '../../components/ServiceCard/ServiceCard';

interface Options {
  variant?: ContentCardVariants | ProductCardVariants | ServiceCardVariants;
}

export type Node = (
  | InfoCardModel
  | MetaCardModel
  | ContentCardModel
  | ProductCardModel
  | CardContentCardModel
  | CardInfoCardModel
  | CardMetaCardModel
  | CardProductCardModel
  | ServiceCardModel
  | CardServiceCardModel
) & {
  __contentType?: string;
  __component?: string;
  __eager?: { state: 'partial' | 'loaded' | 'failed' };
};

export function CardConsumer({ node: maybeLazyNode, options }: { node: Node; options?: Options }): React.ReactElement {
  const [node, setNode] = useState(maybeLazyNode);

  useEffect(() => {
    let mounted = true;

    if (typeof maybeLazyNode !== 'object') {
      return () => {
        mounted = false;
      };
    }

    if (maybeLazyNode && '__eager' in maybeLazyNode && maybeLazyNode.__eager.state === 'partial') {
      const url = new URL(window.location.origin);
      url.pathname = '/api/eager';
      url.searchParams.set('id', maybeLazyNode.id);
      url.searchParams.set(maybeLazyNode['publication_state'], maybeLazyNode.id);
      url.searchParams.set('model', maybeLazyNode.__contentType);

      fetch(url.toString())
        .then((response) => response.json())
        .then((data) => {
          if (mounted) {
            setNode(data.node);
          }
        });
    } else if (JSON.stringify(node) !== JSON.stringify(maybeLazyNode)) {
      setNode(maybeLazyNode);
    }
    return () => {
      mounted = false;
    };
  }, [maybeLazyNode]);

  if (!node) {
    return null;
  }

  if (typeof node === 'number') {
    // eslint-disable-next-line no-console
    console.error('Struggled to dereference a card object', node);
    return null;
  }

  if (node && '__eager' in node && node.__eager.state === 'partial') {
    return (
      <>
        <Skeleton animation="wave" variant="rectangular" width="100%" sx={{ aspectRatio: `16/9`, height: 'auto' }} />
        <Skeleton animation="wave" variant="text" width="100%" height="42" />
        <Skeleton animation="wave" variant="text" width="100%" height="100" />
      </>
    );
  }

  if (isCardContentCard(node)) {
    return <CardConsumer node={node.contentCard} options={options} />;
  }

  if (isCardInfoCard(node)) {
    return <CardConsumer node={node.infoCard} options={options} />;
  }

  if (isCardMetaCard(node)) {
    return <CardConsumer node={node.metaCard} options={options} />;
  }

  if (isCardProductCard(node)) {
    return <CardConsumer node={node.productCard} options={options} />;
  }

  if (isCardServiceCard(node)) {
    return <CardConsumer node={node.serviceCard} options={options} />;
  }

  if (isInfoCard(node)) {
    return <InfoCard node={node} />;
  }

  if (isMetaCard(node)) {
    return <MetaCard node={node} />;
  }

  if (isContentCard(node)) {
    return <ContentCard node={node} variant={options?.variant as ContentCardVariants} />;
  }

  if (isProductCard(node)) {
    return <ProductCard node={node} variant={options?.variant as ProductCardVariants} />;
  }

  if (isServiceCard(node)) {
    return <ServiceCard node={node} variant={options?.variant as ServiceCardVariants} />;
  }

  return null;
}
